import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { ReactComponent as DeleteIcon } from '../../assets/deleteIcon.svg'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import DeleteConfirmation from '../Confirmation'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import {
  fetchDesignations,
  deleteDesignation,
} from '../../slices/designationsApiSlice'

const Designations = ({
  selectedDesignation,
  onSelectDesignation,
  onDeleteDesignation,
  onAddNew,
  setIsAdding,
  setSelectedDesignation,
}) => {
  const { list: designations } = useSelector((state) => state.designations)

  const dispatch = useDispatch()

  const [selectedDesignationId, setSelectedDesignationId] = useState(null)

  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState()
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')

  useEffect(() => {
    dispatch(fetchDesignations())
  }, [dispatch])

  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage

  const filteredDesignations = designations.filter(
    (designation) =>
      designation.name &&
      designation.name.toLowerCase().includes(searchTerm.toLowerCase())
  )
  // Pagination of filtered data
  const paginatedDesignations = filteredDesignations.slice(startIndex, endIndex)
  const totalPages = Math.ceil(filteredDesignations.length / rowsPerPage)

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleSkipToFirst = () => {
    setCurrentPage(1)
  }

  const handleSkipToLast = () => {
    setCurrentPage(totalPages)
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value)
    setCurrentPage(1)
  }

  const handleDeleteClick = (id) => {
    setSelectedId(id)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleConfirmDelete = () => {
    dispatch(deleteDesignation(selectedId))
      .unwrap()
      .then((response) => {
        if (
          response.data &&
          response.data.status ===
            "Designation can't be deleted it is mapped with Associate."
        ) {
          setSnackbarMessage(
            "Designation can't be deleted as it is mapped with an associate."
          )
          setSnackbarSeverity('error')
        } else {
          setSnackbarMessage('Designation deleted successfully')
          setSnackbarSeverity('success')
          dispatch(fetchDesignations())

          setIsAdding(true)
          setSelectedDesignation(null)
        }
      })
      .catch((error) => {
        setSnackbarMessage(error || 'Failed to delete designation')
        setSnackbarSeverity('error')
      })
      .finally(() => {
        setSnackbarOpen(true)
        setOpen(false)
      })
  }

  const handleSelectedDesignation = (designation) => {
    setSelectedDesignationId(designation.id)
    if (onSelectDesignation) {
      onSelectDesignation(designation)
    }
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  const formatDate = (timestamp) => {
    const date = new Date(timestamp)
    const day = date.getDate()
    const daySuffix = (day) => {
      if (day > 3 && day < 21) return 'th'
      switch (day % 10) {
        case 1:
          return 'st'
        case 2:
          return 'nd'
        case 3:
          return 'rd'
        default:
          return 'th'
      }
    }

    const month = date.toLocaleString('en-US', { month: 'short' })
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${day}${daySuffix(day)} ${month} ${year} ${hours}:${minutes}`
  }

  return (
    <Box
      sx={{
        backgroundColor: '#F6F9FE',
        padding: 1,
        borderRadius: '20px',
        boxShadow: 1,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* Title and Add New Button, box */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          pt: 1,
        }}>
        <Typography variant='h5' sx={{ color: '#F5774D', fontWeight: 600 }}>
          Designations
        </Typography>
        <Button
          variant='contained'
          sx={{
            backgroundColor: '#F5774D',
            color: '#FFFFFF',
            borderRadius: '20px',
            textTransform: 'none',
            px: 3,
            '&:hover': {
              backgroundColor: '#FF5C33',
            },
          }}
          onClick={onAddNew}>
          Add New
        </Button>
      </Box>

      {/* Search Bar and Dropdown box */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            boxShadow: 3,
            borderRadius: '50px',
            p: 1,
            backgroundColor: '#FFFFFF',
            flexGrow: 1,
            mr: 2,
          }}>
          <SearchIcon sx={{ fontSize: '20px' }} />
          <TextField
            variant='standard'
            placeholder='Search...'
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              disableUnderline: true,
              inputProps: {
                style: {
                  padding: 0,
                },
              },
            }}
            sx={{ ml: 1, height: '20px', width: '100%' }}
          />
        </Box>
        <Box>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            displayEmpty
            IconComponent={ArrowDropDownIcon}
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              borderRadius: '2px',
              px: 2,
              py: 0,
              height: '36px',
              boxShadow: 3,
            }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Designations List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          maxHeight: '70vh',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#AAB4C6',
            borderRadius: '10px',
            border: '2px solid #f1f1f1',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#555',
          },
        }}>
        {paginatedDesignations.map((designation) => (
          <Paper
            key={designation.id}
            onClick={() => handleSelectedDesignation(designation)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              mr: 1,
              p: 1,
              backgroundColor:
                selectedDesignationId === designation.id
                  ? '#FFA88D80'
                  : '#FFFFFF',
              borderRadius: 5,
              boxShadow: 0,
              '&:hover': {
                backgroundColor:
                  selectedDesignationId === designation.id
                    ? '#FFA88D80'
                    : '#FDE3DB99',
                '& .designation-name': { color: '#081332' },
              },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '20px' }}>
              <Box
                sx={{
                  display: 'flex',
                  fontFamily: 'Work Sans',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  color: '#081332',
                  borderRadius: '50%',
                  border: '1px solid',
                  mr: 2,
                  width: '43.5px',
                  height: '43.5px',
                  borderColor: '#F5774D',
                }}>
                <Typography
                  variant='body2'
                  sx={{
                    alignSelf: 'center',
                    textAlign: 'center',
                    width: '100%',
                  }}>
                  {designation.code}
                </Typography>
              </Box>

              {/* Name and percentage */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    maxWidth: '300px',
                    minWidth: '300px',
                  }}>
                  <Typography
                    variant='body1'
                    className='designation-name'
                    sx={{
                      fontFamily: 'Work Sans',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      color: '#081332',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '80%',
                    }}>
                    {designation.name}
                  </Typography>
                  <Typography
                    sx={{
                      paddingLeft: '5px',
                      flexShrink: 0,
                    }}>{`(${designation.percentage}%)`}</Typography>
                </Box>

                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#5E6066',
                  }}>
                  Last modified:{' '}
                  {designation.modifiedDate
                    ? formatDate(designation.modifiedDate)
                    : formatDate(designation.createdDate)}
                </Typography>
              </Box>
            </Box>
            <IconButton
              sx={{ color: '#F5774D' }}
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClick(designation.id)
              }}>
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Box>
          <Typography
            variant='body1'
            sx={{ fontWeight: 'bold', color: '#000000' }}>
            {`${
              filteredDesignations.length === 0 ? 0 : startIndex + 1
            } - ${Math.min(endIndex, filteredDesignations.length)} of ${
              filteredDesignations.length
            } records`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ color: currentPage === 1 ? '#FFA88D80' : '#F6764E' }}
            onClick={handleSkipToFirst}
            disabled={currentPage === 1}>
            <SkipPreviousIcon />
          </IconButton>

          <IconButton
            sx={{ color: currentPage === 1 ? '#FFA88D80' : '#F6764E' }}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}>
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            sx={{ color: '#F6764E' }}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}>
            <ChevronRightIcon />
          </IconButton>

          <IconButton
            sx={{ color: '#F6764E' }}
            onClick={handleSkipToLast}
            disabled={currentPage === totalPages}>
            <SkipNextIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirmDelete}
        message={'Are you sure? Do you want to delete?'}
      />

      {/* Snackbar  */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}>
          {snackbarMessage}
          <LinearProgress
            variant='determinate'
            value={100}
            sx={{
              animation: 'progressBar 4s linear forwards',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              boxSizing: 'border-box',
              transformOrigin: 'right',
            }}
          />
        </Alert>
      </Snackbar>
      <style>
        {`
    @keyframes progressBar {
      0% { transform: scaleX(1); }
      100% { transform: scaleX(0); }
    }
  `}
      </style>
    </Box>
  )
}

export default Designations
